import { DATE_TIME_FORMAT_DEFAULT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import RolesService from 'api/roles/RolesService';
import UsersService from 'api/users/UsersService';
import { UserDto } from 'api/users/models/UserDto';
import Button from 'common/components/button/Button';
import DateFormat from 'common/components/dateFormat/dateFormat';
import ListingTable from 'common/components/listingTable/ListingTable';
import ListingTableCache, { ListingTableData } from 'common/components/listingTable/ListingTableCache';
import TableActionButton from 'common/components/listingTable/actionButton/TableActionButton';
import { FilterData, ListingTableColumn, OrderData } from 'common/components/listingTable/models';
import ListingTableSidebar from 'common/components/listingTable/sidebar/ListingTableSidebar';
import { orderColumns } from 'common/components/listingTable/tableUtils';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Toast from 'common/services/Toast';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Reducers } from 'store/types';

interface Filters {
    realName?: string;
    email?: string;
    phoneNumber?: string;
    roleId?: string;
    active?: boolean;

    updatedDate?: { start: Date | null; end: Date | null };
    updatedDateStart?: Date | null;
    updatedDateEnd?: Date | null;

    userUpdatedName?: string;
}

const PAGE_ID = 'BACKOFFICE_USERS_LIST';

function UsersListScreen(): JSX.Element | null {
    const baseUrl = '/backoffice/users'

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState<UserDto[]>([]);
    const [tableData, setTableData] = useState<ListingTableData<UserDto, Filters>>(ListingTableCache.get(PAGE_ID, {
        order: { field: 'realName', orderColumn: 'real_name', isOrderAsc: true },
        hiddenColumns: [],
        columnsOrder: [],
        columnsWidth: [],
        page: 1,
        filters: {},
        lastRowId: null,
    }));
    const [roles, setRoles] = useState<SelectInputOption[]>();
    const [userToDelete, setUserToDelete] = useState<UserDto | null>(null);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['USERS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['USERS_READ'])
    const [totalItems, setTotalItems] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);
    const firstLoad = useRef(true);

    const columns: ListingTableColumn<UserDto>[] = orderColumns([
        {
            name: t('users.list.name'),
            field: 'realName',
            filter: { type: 'text', value: tableData.filters.realName ?? '', placeholder: t('users.list.name') },
            minWidth: '18rem',
            orderColumn: 'real_name'
        },
        {
            name: t('users.list.contact'),
            field: 'email',
            filter: { type: 'text', value: tableData.filters.email ?? '', placeholder: t('users.list.contact') },
            minWidth: '18rem',
        },
        {
            name: t('users.list.phone_number'),
            field: 'phoneNumber',
            filter: { type: 'text', value: tableData.filters.phoneNumber ?? '', placeholder: t('users.list.phone_number') },
            minWidth: '18rem',
            orderColumn: 'phone_number',
            renderCell: (row) => {
                return <div>{row.phoneCode ? `+${row.phoneCode}` : ''} {row.phoneNumber}</div>;
            }
        },
        {
            name: t('users.list.role'),
            field: 'roleId',
            filter: { type: 'dropdown', value: tableData.filters.roleId ?? '', options: roles || [] },
            minWidth: '18rem',
            orderColumn: 'r.description',
            renderCell: (row) => row.roleName ?? ''
        },
        {
            name: t('common.updated_date'),
            field: 'updatedDate',
            filter: {
                type: 'date-range',
                value: {
                    start: tableData.filters.updatedDate?.start ? new Date(tableData.filters.updatedDate.start) : null,
                    end: tableData.filters.updatedDate?.end ? new Date(tableData.filters.updatedDate.end) : null
                }
            },
            minWidth: '22rem',
            orderColumn: 'updated_date',
            renderCell: (row) => {
                return row.updatedDate ? <DateFormat value={row.updatedDate} format={DATE_TIME_FORMAT_DEFAULT} /> : '';
            }
        },
        {
            name: t('common.user_updated'),
            field: 'userUpdatedName',
            filter: { type: 'text', value: tableData.filters.userUpdatedName ?? '', placeholder: t('common.user_updated') },
            minWidth: '21rem',
            orderColumn: 'user_updated_name',
        },
    ], tableData.columnsOrder);

    const onFilter = (col: ListingTableColumn<UserDto>, filterData: FilterData) => {
        setRefreshKey(k => k + 1);
        setTableData(d => {
            const data = ({
                ...d, page: 1
            })
            if (col.field === 'realName') {
                data.filters = {
                    ...data.filters,
                    realName: filterData.value as string,
                };
            }
            if (col.field === 'email') {
                data.filters = {
                    ...data.filters,
                    email: filterData.value as string,
                };
            }
            if (col.field === 'phoneNumber') {
                data.filters = {
                    ...data.filters,
                    phoneNumber: filterData.value as string,
                };
            }
            if (col.field === 'roleId') {
                data.filters = {
                    ...data.filters,
                    roleId: filterData.value as string,
                };
            }
            if (col.field === 'updatedDate') {
                data.filters = {
                    ...data.filters,
                    updatedDate: filterData.value,
                    updatedDateStart: filterData.value?.start as Date,
                    updatedDateEnd: filterData.value?.end as Date
                };
            }

            if (col.field === 'userUpdatedName') {
                data.filters = {
                    ...data.filters,
                    userUpdatedName: filterData.value as string
                };
            }
            return data;
        });
    }

    const onOrder = (orderData: OrderData<UserDto>) => {
        setRefreshKey(k => k + 1)
        setTableData(d => ({ ...d, order: orderData, page: 1 }));
    }

    const onClearFilters = () => {
        setTableData(d => ({ ...d, page: 1, filters: {}, lastRowId: null }));
        setRefreshKey(k => k + 1);
    }

    const renderAction = (row: UserDto) => (
        <TableActionButton
            options={[
                { label: <span><FaEdit /> {t('common.edit')}</span>, onClick: () => navigateTo('edit', row.id) },
                { label: <span><FaTrash /> {t('common.delete')}</span>, onClick: () => onOpenModalToDelete(row) },
            ]}
        />
    )

    const onOpenModalToDelete = (row: UserDto) => {
        setUserToDelete(row);
        setShowConfirmDeleteModal(true)
    }

    const getData = async () => {
        if (isLoading) {
            return;
        }

        Loading.show();
        setIsLoading(true);

        try {
            if (!roles?.length) {
                const rolesResult = await RolesService.catalog();
                setRoles(rolesResult);
            }
            const result = await UsersService.getList({
                ...tableData.filters,
                page: firstLoad.current && tableData.page > 1 ? 1 : tableData.page,
                itemsPerPage: firstLoad.current && tableData.page > 1 ? DEFAULT_PAGINATION_ITEMS_PER_PAGE * tableData.page : DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                orderColumn: tableData.order.orderColumn,
                orderBy: tableData.order.isOrderAsc ? 'asc' : 'desc'
            });

            const newListElements = result.currentPage === 1 ? result.items : [...data, ...result.items]
            setHasMore(result.totalItems > newListElements.length);
            setData(newListElements);
            setTotalItems(result.totalItems)

            if (firstLoad.current) {
                setScrollToRowId(tableData.lastRowId);
            }
            firstLoad.current = false;
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get users', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsLoading(false);
            Loading.hide();
        }
    }

    const onLoadMore = () => {
        setTableData(d => ({ ...d, page: d.page + 1 }));
    }

    const onDelete = async (result: boolean) => {
        if (!result) {
            setShowConfirmDeleteModal(false)
            return;
        }

        Loading.show();

        try {
            await UsersService.remove(userToDelete as UserDto)
            Toast.success(t('messages.record_delete_success'));
            setTableData(d => ({ ...d, page: 1 }));
            setRefreshKey(k => k + 1);
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete user', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
        }
        setShowConfirmDeleteModal(false)
    }

    const onRowClick = (id: string) => {
        navigate(baseUrl + '/details/' + id)
    }

    const onClickNew = () => {
        navigate(baseUrl + '/create')
    }

    const navigateTo = (type: string, id?: string) => {
        if (type === 'create') {
            navigate(`${baseUrl}/${type}`);
        } else if (id) {
            ListingTableCache.saveLastRowId(PAGE_ID, id);
            navigate(`${baseUrl}/${type}/${id}`);
        }
    }

    useEffect(() => {
        void getData();
    }, [tableData.page, tableData.filters, tableData.order, refreshKey]);

    useEffect(() => {
        ListingTableCache.save(PAGE_ID, tableData);
    }, [tableData]);

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={t('users.title')}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('users.title'), url: baseUrl },
                ]}
            />
            <PageHeader title={t('users.title')} subTitle={`${totalItems} ${t('common.total_results')}`} informationText={t('users.list.sub_title')} showGoBack={false} addSidebarSpacing>
                {canWrite && <Button onClick={onClickNew}>{t('common.new')}</Button>}
            </PageHeader>
            <PageContainer>
                <ListingTable<UserDto>
                    columns={columns}
                    rows={data}
                    order={tableData.order}
                    hiddenColumns={tableData.hiddenColumns}
                    columnsWidths={tableData.columnsWidth}
                    onOrder={onOrder}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                    onRenderAction={canWrite ? renderAction : undefined}
                    onRowClick={(r) => onRowClick(r.id)}
                    infiniteScroll={{
                        isLoading,
                        hasMore,
                        onLoadMore,
                    }}
                    onRowId={r => r.id}
                    scrollToRowId={scrollToRowId}
                    onColResize={(w) => setTableData(d => ({ ...d, columnsWidth: w }))}
                />
            </PageContainer>
            <ListingTableSidebar
                columns={columns}
                hiddenColumns={tableData.hiddenColumns}
                onChangeHiddenColumns={(hc) => setTableData(d => ({ ...d, hiddenColumns: hc }))}
                onChangeColumnsOrder={(oc) => setTableData(d => ({ ...d, columnsOrder: oc }))}
            />
            <ConfirmDeleteModal
                itemName={userToDelete?.realName}
                isOpen={showConfirmDeleteModal}
                onClose={(result) => onDelete(result)}
            />
        </ScreenTitle>
    );
}

export default UsersListScreen;
